import { IFsFileData, IFsFileType } from "@r3f-component-data-structure";
import { FsAudio, FsFile, FsImage, FsImage360, FsImageTarget, FsModel3D, FsVideo, FsVideo360 } from "./files";

export class Filestore {
    private _files: {[id: string]: FsFile} = {}

    constructor() {

    }

    /** Build the FsFile subclass for one entry. Shared by load() and merge() so the two can't drift. */
    private _create(id: string, data: IFsFileData): FsFile {
        switch (data.type) {
            case IFsFileType.Image:
                return new FsImage(id, data.name)
            case IFsFileType.Video:
                return new FsVideo(id, data.name, data.hasHls !== false)
            case IFsFileType.Audio:
                return new FsAudio(id, data.name)
            case IFsFileType.Model3d:
                return new FsModel3D(id, data.name)
            case IFsFileType.Image360:
                return new FsImage360(id, data.name)
            case IFsFileType.Video360:
                return new FsVideo360(id, data.name, data.hasHls !== false)
            case IFsFileType.ImageTarget:
                return new FsImageTarget(id, data.name)
            default:
                throw new Error(`Unknown file type: ${data.type}`)
        }
    }

    /** Replace every entry. Boot only — see merge() for why an update must not use this. */
    load(data: { [id: string]: IFsFileData }) {
        for (const id in data) {
            this._files[id] = this._create(id, data[id])
        }
    }

    /**
     * Reconcile against a new `mediaFilesById` WITHOUT discarding cached bytes.
     *
     * `load()` overwrites unconditionally, which throws away everything each FsFile has decoded —
     * `FsImage`'s blob and GPU texture, `FsModel3D`'s array buffer and parsed GLTF, `FsImageTarget`'s ZPT
     * and object URL. Calling it on every live update would re-fetch and re-decode EVERY asset in the
     * project on every edit, including the ones nobody touched.
     *
     * Keeping an existing instance is sound because filestore ids are content-addressed: the bytes behind
     * a given id never change, so "same id" implies "same bytes". A swapped asset arrives under a NEW id
     * and is created fresh here.
     *
     * Returns the orphaned instances rather than disposing them: this runs BEFORE the store dispatch, so
     * the components still rendering them have not unmounted yet. The caller disposes once React has
     * flushed — see `Jute.applyContentUpdate`.
     */
    merge(data: { [id: string]: IFsFileData }): { added: string[]; removed: FsFile[] } {
        const added: string[] = []
        const removed: FsFile[] = []

        for (const id in data) {
            if (this._files[id]) continue
            this._files[id] = this._create(id, data[id])
            added.push(id)
        }

        for (const id in this._files) {
            if (data[id]) continue
            removed.push(this._files[id])
            delete this._files[id]
        }

        return { added, removed }
    }

    // loadFiles() {
    //     const promises: Promise<FsFile>[] = []
    //     for (const id in this._files) {
    //         promises.push(this._files[id].load())
    //     }
    //     return Promise.all(promises)
    // }

    get<T extends FsFile>(id?: string) {
        if (typeof id === 'undefined') throw new Error('File ID is required')
        return this._files[id] as T
    }

    getAll() {
        return this._files;
    }

}

// Singletone
export const filestore = new Filestore()
